import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Site } from '../types'

interface SiteState {
  sites: Site[]
  selectedSiteIds: string[]
  isLoading: boolean
  error: string | null
  setSites: (sites: Site[]) => void
  addSite: (site: Site) => void
  updateSite: (id: string, updates: Partial<Site>) => void
  removeSite: (id: string) => void
  setSelectedSiteIds: (ids: string[]) => void
  toggleSite: (id: string) => void
  setSelectedSite: (id: string) => void
  selectAllSites: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

export const useSiteStore = create<SiteState>()(
  persist(
    (set, get) => ({
      sites: [],
      selectedSiteIds: [],
      isLoading: false,
      error: null,

      setSites: (sites) =>
        set({
          sites,
          // Auto-select all sites on first load if none selected
          selectedSiteIds:
            get().selectedSiteIds.length > 0
              ? get().selectedSiteIds
              : sites.map((s) => s.id),
        }),

      addSite: (site) =>
        set((state) => ({
          sites: [...state.sites, site],
          selectedSiteIds: [...state.selectedSiteIds, site.id],
        })),

      updateSite: (id, updates) =>
        set((state) => ({
          sites: state.sites.map((site) =>
            site.id === id ? { ...site, ...updates } : site
          ),
        })),

      removeSite: (id) =>
        set((state) => ({
          sites: state.sites.filter((site) => site.id !== id),
          selectedSiteIds: state.selectedSiteIds.filter((sid) => sid !== id),
        })),

      setSelectedSiteIds: (ids) => set({ selectedSiteIds: ids }),

      toggleSite: (id) =>
        set((state) => {
          const isSelected = state.selectedSiteIds.includes(id)
          return {
            selectedSiteIds: isSelected
              ? state.selectedSiteIds.filter((sid) => sid !== id)
              : [...state.selectedSiteIds, id],
          }
        }),

      setSelectedSite: (id) => set({ selectedSiteIds: [id] }),

      selectAllSites: () =>
        set((state) => ({
          selectedSiteIds: state.sites.map((s) => s.id),
        })),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),
    }),
    {
      name: 'site-storage',
      partialize: (state) => ({
        selectedSiteIds: state.selectedSiteIds,
      }),
    }
  )
)
